import React from 'react'

import { Controller } from 'react-hook-form'

import {
  Button,
  TextInput,
  Toggle,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, bool } from 'prop-types'

import useExplicitMarketingConsent from 'Pods/Shared/Hooks/useExplicitMarketingConsent'
import I18n from 'Pods/Shared/I18n'
import FacebookIcon from 'Pods/Shared/Icons/FacebookIcon'
import GoogleIcon from 'Pods/Shared/Icons/GoogleIcon'
import ReactPasswordStrengthNew from 'Pods/Shared/ReactPasswordStrengthNew'

import MTCaptcha from 'Utils/MTCaptcha'

import { useRegister } from './Hooks/useRegister'

// Password registration kept for the checkout modal (UserV2/UserModalWithTabs),
// which stays outside the OTP AuthFlow rollout - useRegister handles the
// guest-checkout source, cart merge, and return to /cg/checkout/payment.
function Register({
  customId = '',
  captchaId = '',
  source = null,
  fromCheckout = false,
  locale = 'en',
  showEmailAvailability = false,
}) {
  const translate = (key) => I18n.t(key, { ns: 'rails', lng: locale })

  const {
    token,
    control,
    gdpr,
    registrationCaptchaEnabled,
    passwordError,
    handleSubmit,
    onFormSubmit,
    onPasswordChange,
    onGdprToggle,
    omniAuthParams,
    getEmailHelpText,
    isEmailWarning,
    isEmailSuccess,
  } = useRegister({
    captchaId,
    source,
    fromCheckout,
    showEmailAvailability,
    locale,
  })

  const explicitConsentRequired = useExplicitMarketingConsent()
  const consentPending = explicitConsentRequired === null

  return (
    <div className="user-auth-container">
      <div className="social-buttons-container social-buttons-container--top">
        <div className="row">
          <div className="col-6">
            <form action={encodeURI(`/users/auth/google_oauth2?${omniAuthParams()}`)} method="post">
              <Button
                expand
                size="large"
                icon={<GoogleIcon />}
                type="primary"
                variant="only-icon"
                disabled={consentPending}
                customClass={classnames(
                  'cgt-button--google-plus',
                  'justify-content-center'
                )}
              />
              {token && <input type="hidden" name="authenticity_token" value={token} />}
            </form>
          </div>
          <div className="col-6">
            <form action={encodeURI(`/users/auth/facebook?${omniAuthParams()}`)} method="post">
              <Button
                expand
                size="large"
                icon={<FacebookIcon />}
                type="primary"
                variant="only-icon"
                disabled={consentPending}
                customClass={classnames(
                  'cgt-button--facebook',
                  'justify-content-center'
                )}
              />
              {token && <input type="hidden" name="authenticity_token" value={token} />}
            </form>
          </div>
        </div>
      </div>

      <div className="auth-divider">
        <span>{translate('register.divider')}</span>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: translate('register.validation.email_required'),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: translate('register.validation.email_invalid'),
            },
          }}
          render={({ field }) => (
            <TextInput
              label={translate('register.email_label')}
              id={`email${customId}`}
              name="email"
              isWarning={isEmailWarning()}
              helpText={getEmailHelpText()}
              value={field.value}
              wrapperClassName={classnames(
                'auth-tab__field',
                isEmailSuccess() && 'auth-tab__field--success'
              )}
              handleChange={field.onChange}
              onBlur={field.onBlur}
              isPilot
              isRequired
            />
          )}
        />
        {/* Honey pot field */}
        <Controller
          name="email_confirmation"
          control={control}
          render={({ field }) => (
            <TextInput
              label={translate('register.email_confirmation_label')}
              id={`email_confirmation${customId}`}
              name="email_confirmation"
              value={field.value}
              wrapperClassName="auth-tab__field hidden"
              handleChange={field.onChange}
              autoComplete="off"
              tabIndex={-1}
              isPilot
              isRequired
            />
          )}
        />
        <ReactPasswordStrengthNew
          helpText={passwordError}
          hasError={!!passwordError}
          minLength={6}
          className={classnames(
            'cgt-text-input',
            'cgt-text-input--medium',
            'cgt-text-input--primary',
            'cgt-text-input--icon-trail',
            'cgt-text-input--not-spaced',
            'cgt-text-input--password',
            'has-no-bg'
          )}
          inputProps={{
            id: `password${customId}`,
            name: 'password',
          }}
          changeCallback={onPasswordChange}
          showToggleVisibility
        />

        {explicitConsentRequired && (
          <Toggle
            id={`gdpr-toggle${customId}`}
            label={translate('register.gdpr_label')}
            isChecked={gdpr}
            onChange={onGdprToggle}
            className="gdpr-toggle"
          />
        )}

        {registrationCaptchaEnabled && <MTCaptcha captchaId={captchaId} />}

        <Button
          expand
          size="large"
          actionType="submit"
          disabled={consentPending}
        >
          {translate('register.submit_button')}
        </Button>

        <p className={classnames(
          'cgt-legacy-text-tiny',
          'cgt-legacy-text--color-secondary-opaque',
          'temp-terms',
          'u-text-center',
          'cgt-mt-16',
          'cgt-mb-0'
        )}>
          {translate('register.terms_prefix')}{' '}
          <a href="/pages/terms-and-conditions" target="_blank" className="cgt-legacy-link tiny-link">
            {translate('register.terms_link')}
          </a>
          {' '}{translate('register.terms_middle')}{' '}
          <a className="cgt-legacy-link tiny-link" target="_blank" href="/pages/privacy-policy">
            {translate('register.privacy_link')}
          </a>
        </p>
      </form>
    </div>
  )
}

Register.propTypes = {
  customId: string,
  captchaId: string,
  source: string,
  fromCheckout: bool,
  locale: string,
  showEmailAvailability: bool,
}

export default Register
